import { useBack } from "@website/hooks/back";
import { USER_TRACKING_EVENTS } from "@website/tracking/tracking.types";
import { addEventToTracking } from "@website/tracking/user";
import { getCarrierByPhoneNumber } from "@website/utils/mobile-carrier-map";
import { replaceSnappTripBasedOnChannel } from "@website/utils/title-replace";
import { PhoneNumberCardPropertiesType } from "@website/widget/login/components/phone-number-card/phone-number-card.types";
import { FORCE_LOGIN_KEY } from "@website/widget/login/constants";
import { AuthRequestOtpService } from "@website/widget/login/services/auth-request-otp";
import { SendOtpService } from "@website/widget/login/services/send-otp";
import {
  isPhoneNumberPartiallyValid,
  isPhoneNumberValid,
  toEnglishDigits
} from "@website/widget/login/utils";

import { useWelcomeScreen } from "@website/widget/login/utils/useWelcomeScreen";
import { useRouter } from "next/router";
import { ChangeEvent, useCallback, useState } from "react";

export const usePhoneNumberCard = (
  properties: PhoneNumberCardPropertiesType
) => {
  const { setStep, setUserData, userData } = properties;
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [phoneNumber, setPhoneNumber] = useState<string>("");
  const router = useRouter();
  const isForceLogin = Boolean(router.query[FORCE_LOGIN_KEY]);
  const { goBack } = useBack();
  const { isWelcomeScreen } = useWelcomeScreen();

  const headerTitle = replaceSnappTripBasedOnChannel("ورود به اسنپ‌تریپ");
  const modalTitle = isWelcomeScreen
    ? "سریع‌، هوشمندانه و مطابق سلیقه خودتان رزرو کنید."
    : "برای ادامه لطفا شماره موبایل خود را وارد کنید.";

  const onChangePhoneNumber = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target;
      const normalizedValue = toEnglishDigits(value);
      if (isPhoneNumberPartiallyValid(normalizedValue)) {
        setPhoneNumber(normalizedValue);
      }
    },
    []
  );

  const onSubmitPhoneNumber = useCallback(
    async (_event: React.FormEvent<HTMLFormElement>) => {
      _event.preventDefault();
      if (isPhoneNumberValid(phoneNumber)) {
        setIsLoading(true);
        setError("");
        addEventToTracking(USER_TRACKING_EVENTS.CREDENTIAL_ENTERED, {
          product: "core",
          carrier: getCarrierByPhoneNumber(phoneNumber)
        });
        SendOtpService({ phoneNumber })
          .then((data) => {
            if (data?.availableAuthMethods.includes("password")) {
              setIsLoading(false);
              setStep("password");
              setUserData({ ...userData, phoneNumber });
              // eslint-disable-next-line no-dupe-else-if
            } else if (data?.availableAuthMethods.includes("password")) {
              setError("نمیتوانید وارد شوید. لطفا با پشتیبانی تماس بگیرید.");
            } else {
              setStep("otp");
              AuthRequestOtpService({ phoneNumber })
                .then((data) => {
                  setUserData({ ...userData, ...data, phoneNumber });
                  if (isWelcomeScreen) {
                    addEventToTracking(
                      USER_TRACKING_EVENTS.APP_WELCOME_SCREEN_SKIP_CLICKED,
                      { product: "core" }
                    );
                    localStorage.setItem(
                      "lastTimeWelcomeScreenShowed",
                      new Date().toISOString()
                    );
                  }
                })
                .catch((error) => {
                  setError(
                    error?.response?.data?.message ||
                      "خطایی رخ داده، دوباره تلاش کنید."
                  );
                })
                .finally(() => setIsLoading(false));
            }
          })
          .catch((error) => {
            setError(
              error?.response?.data?.message ||
                "خطایی رخ داده، دوباره تلاش کنید."
            );
          })
          .finally(() => setIsLoading(false));
      } else {
        setError("شماره تلفن معتبر نیست.");
      }
    },
    [phoneNumber, setStep, setUserData, userData]
  );

  const shouldRedirectToHome =
    typeof window !== "undefined" &&
    (router.basePath.includes("/profile") ||
      window.history.length <= 1 ||
      (document.referrer && !document.referrer.includes(window.location.host)));

  const backButtonText = shouldRedirectToHome
    ? "بازگشت به صفحه اصلی"
    : "بازگشت";

  const loginBack = useCallback(() => {
    if (shouldRedirectToHome) {
      window.location.href = "/";
    } else {
      goBack();
    }
  }, [shouldRedirectToHome, goBack]);

  return {
    error,
    isLoading,
    phoneNumber,
    onChangePhoneNumber,
    onSubmitPhoneNumber,
    headerTitle,
    modalTitle,
    loginBack,
    isForceLogin,
    backButtonText
  };
};
